import type { Store } from '../../../../services/stores/stores-sdk'
import { createStoreDetailUrl } from '../../StoreDetails/utils'

interface StoreLocatorLDJsonParams {
  stores?: Store[]
  pageName?: string
  pageDescription?: string
  pageUrl?: string
  storeDetailPageUrl?: string
  storeImageUrl?: string
  enableStoreSpecificImages?: boolean
  validatedImages?: Map<string, string> | Record<string, string>
}

interface StoreItemElement {
  '@type': 'Store'
  name: string
  image?: string
  address?: {
    '@type': 'PostalAddress'
    streetAddress?: string
    addressLocality?: string
    postalCode?: string
    addressCountry?: string
  }
  telephone?: string
  geo?: {
    '@type': 'GeoCoordinates'
    latitude: number
    longitude: number
  }
  url?: string
}

interface GetStoreImageOptions {
  store: Store
  enableStoreSpecificImages: boolean
  imagesMap: Map<string, string>
  storeImageUrl?: string
}

const getStoreImage = (options: GetStoreImageOptions): string | undefined => {
  const { store, enableStoreSpecificImages, imagesMap, storeImageUrl } = options
  const storeSpecificImage =
    enableStoreSpecificImages && store.storeId
      ? imagesMap.get(store.storeId)
      : undefined
  return storeSpecificImage ?? storeImageUrl
}

const buildStoreAddress = (store: Store): StoreItemElement['address'] => {
  if (!store.address) {
    return undefined
  }

  const { addressLine1, city, postalCode, countryCode } = store.address
  if (!addressLine1 && !city && !postalCode && !countryCode) {
    return undefined
  }

  return {
    '@type': 'PostalAddress',
    ...(addressLine1 && { streetAddress: addressLine1 }),
    ...(city && { addressLocality: city }),
    ...(postalCode && { postalCode }),
    ...(countryCode && { addressCountry: countryCode })
  }
}

const buildGeoCoordinates = (store: Store): StoreItemElement['geo'] => {
  const location = store.address?.location
  if (!location) {
    return undefined
  }

  const { latitude, longitude } = location
  if (latitude === undefined || longitude === undefined) {
    return undefined
  }

  return {
    '@type': 'GeoCoordinates',
    latitude,
    longitude
  }
}

const buildStoreUrl = (
  store: Store,
  storeDetailPageUrl?: string
): string | undefined => {
  if (!storeDetailPageUrl) {
    return undefined
  }

  return createStoreDetailUrl(store, storeDetailPageUrl) ?? undefined
}

export const generateStoreLocationLDJson = (
  params: StoreLocatorLDJsonParams
) => {
  const {
    stores = [],
    pageName = '',
    pageDescription = '',
    pageUrl,
    storeDetailPageUrl,
    storeImageUrl,
    enableStoreSpecificImages,
    validatedImages
  } = params

  let imagesMap = new Map<string, string>()
  if (validatedImages instanceof Map) {
    imagesMap = validatedImages
  } else if (validatedImages) {
    imagesMap = new Map(Object.entries(validatedImages))
  }

  const itemListElement: StoreItemElement[] = stores.map((store) => {
    const storeItem: StoreItemElement = {
      '@type': 'Store',
      name: store.name ?? 'Store'
    }

    const imageUrl = getStoreImage({
      store,
      enableStoreSpecificImages: enableStoreSpecificImages ?? false,
      imagesMap,
      storeImageUrl
    })
    if (imageUrl) {
      storeItem.image = imageUrl
    }

    const address = buildStoreAddress(store)
    if (address) {
      storeItem.address = address
    }

    if (store.phone) {
      storeItem.telephone = store.phone
    }

    const geo = buildGeoCoordinates(store)
    if (geo) {
      storeItem.geo = geo
    }

    const url = buildStoreUrl(store, storeDetailPageUrl)
    if (url) {
      storeItem.url = url
    }

    return storeItem
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageName,
    description: pageDescription,
    url: pageUrl,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement
    }
  }
}
